<template>
  <AuthPage v-if="newUiAuthEnabled">
    <UiLoading v-if="isPending" />

    <template v-else>
      <div class="auth-card__center">
        <h1 class="auth-card__welcome">{{ t("Welcome") }}</h1>

        <p class="auth-card__subtitle">{{ t("Please choose an option to continue") }}</p>
      </div>

      <div class="auth-option">
        <span>{{ t("Already have an account?") }}</span>

        <UiButton variant="primary" :icon="LogIn" class="ui-button--block" @click="goToLogin">
          {{ t("Login") }}
        </UiButton>
      </div>

      <template v-if="data?.parents?.allowSelfRegistration">
        <div class="auth-divider">{{ t("or") }}</div>

        <div class="auth-option">
          <span>{{ t("Don't have an account?") }}</span>

          <UiButton
            variant="secondary"
            :icon="UserPlus"
            class="ui-button--block"
            @click="router.push({ name: 'auth.register' })"
          >
            {{ t("Register") }}
          </UiButton>
        </div>
      </template>
    </template>
  </AuthPage>

  <div v-else class="app flex-row align-items-center">
    <div class="container">
      <b-row class="justify-content-center animated fadeIn">
        <b-col sm="12" md="10" lg="8" class="text-center">
          <img :src="logo" :alt="logoAlt" class="login-form-image mb-4" />

          <h1 class="mb-4">{{ t("Welcome") }}</h1>

          <b-card no-body class="card-accent-primary p-3 p-sm-4">
            <div
              v-if="isPending"
              class="d-flex justify-content-center align-items-center"
              style="min-height: 200px"
            >
              <Loading />
            </div>

            <div v-else>
              <p class="text-muted mb-4">{{ t("Please choose an option to continue") }}</p>

              <div class="row g-3 justify-content-center">
                <!-- Login Button -->
                <div class="col-auto">
                  <div class="auth-option text-center">
                    <small class="text-muted d-block mb-2">{{
                      t("Already have an account?")
                    }}</small>
                    <b-button :href="loginUrl" variant="primary" size="lg">
                      <UiIcon fa="fas fa-sign-in-alt me-2" />
                      {{ t("Login") }}
                    </b-button>
                  </div>
                </div>

                <!-- Register Button -->
                <div v-if="data?.parents?.allowSelfRegistration" class="col-auto">
                  <div class="auth-option text-center">
                    <small class="text-muted d-block mb-2">{{ t("Create a new account") }}</small>
                    <b-button :to="{ name: 'auth.register' }" variant="outline-primary" size="lg">
                      <UiIcon fa="fas fa-user-plus me-2" />
                      {{ t("Register") }}
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { LOGIN_URL } from "@/config";

import { computed, watchEffect } from "vue";
import { usePublicTenantSettings } from "@/modules/settings/queries/usePublicTenantSettings";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import Loading from "@/components/Loading.vue";
import { LogIn, UserPlus } from "lucide-vue-next";
import AuthPage from "../components/newUi/AuthPage.vue";
import { useNewUiEnabled } from "@/components/newUi/core/useNewUiEnabled";

// forceEnabled: must load even for a visitor "authenticated" on another tenant's stale cookie
const { data, isPending } = usePublicTenantSettings({ forceEnabled: true });
const { t } = useI18n();
const newUiAuthEnabled = useNewUiEnabled();
const router = useRouter();

const logo = computed(() => "/img/obiado.svg");
const logoAlt = computed(() => "Obiado logo");
const loginUrl = computed(() => `${LOGIN_URL}`);

const goToLogin = (): void => {
  window.location.href = loginUrl.value;
};

watchEffect(() => {
  if (isPending.value) {
    return;
  }
  // if registration is disabled, redirect to login page
  if (!data.value?.parents?.allowSelfRegistration && loginUrl.value) {
    window.location.href = loginUrl.value;
  }
});
</script>

<style scoped lang="scss">
.auth-option .btn-outline-primary:hover {
  color: white;
}
</style>
